import React, { useId, useRef, useState } from "react";

import { ScreenIntro } from "../PatternSections";
import { relearnPattern, undoLearning } from "../useLearning";
import CatalogMatch from "./CatalogMatch";
import DiagnosisStats from "./DiagnosisStats";
import SessionContext from "./SessionContext";
import useSessionContext, { sessionIdOf } from "./useSessionContext";
import { CATALOG_STATE, CATALOG_TEXT, errorText, fullWhen } from "./catalogText";

const C = CATALOG_TEXT.card;
const A = CATALOG_TEXT.actions;

/** The actions the backend allows on the entry: Edit, Learn again, Undo with an in-place confirm; one request at a time. */
function Actions({ item, onChanged }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false); // a double click lands before `busy` disables the button: one request per action
  const undone = item.state === CATALOG_STATE.UNDONE;
  const canRelearn = undone && !item.published;

  const run = async (call, notice) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      await call(item.id);
      setConfirming(false);
      onChanged(notice);
    } catch (e) {
      setError(errorText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      {error && <p className="le-error" role="alert">{error}</p>}
      {confirming ? (
        <div className="le-confirm" role="group" aria-label={A.undo}>
          <p className="le-text">{A.undoQuestion}</p>
          <div className="le-actions">
            <button type="button" className="le-btn le-btn--danger" disabled={busy}
              onClick={() => run(undoLearning, CATALOG_TEXT.notices.undone)}>{busy ? A.working : A.confirmUndo}</button>
            <button type="button" className="le-btn" disabled={busy}
              onClick={() => { setConfirming(false); setError(""); }}>{A.cancel}</button>
          </div>
        </div>
      ) : (
        <div className="le-actions">
          {canRelearn && (
            <button type="button" className="le-btn le-btn--primary" disabled={busy}
              onClick={() => run(relearnPattern, CATALOG_TEXT.notices.relearned)}>{busy ? A.working : A.relearn}</button>
          )}
          {!undone && (
            <button type="button" className="le-btn" onClick={() => { setError(""); setConfirming(true); }}>{A.undo}</button>
          )}
        </div>
      )}
    </>
  );
}

/**
 * The detail popup's body for one learned pattern, laid out like the Learning editor's Review screen: the title block (product ·
 * category, who saved it, tags saying whether it is in the graph, undone or read-only), where it came from (the
 * "Taught manually" source block) and what its chat recorded (SessionContext), the pattern as matched rows from the stored
 * snapshot (CatalogMatch), the rough figures of the diagnosis it was taught on (DiagnosisStats; one request shared with
 * SessionContext), and the actions. `onChanged(notice)` runs after an
 * undo or relearn succeeded. A learned pattern is not edited here: undo it, then teach it again.
 */
export default function CatalogDetail({ item, onChanged }) {
  const id = useId();
  const context = useSessionContext(sessionIdOf(item));
  const undone = item.state === CATALOG_STATE.UNDONE;
  const tags = [
    item.published && { label: C.graph },
    undone && { label: C.undone, tone: "muted" },
  ];
  const category = [item.product, item.category_label || item.category].filter(Boolean).join(" · ");
  const manual = { savedBy: item.saved_by, savedAt: item.saved_at, category, caseId: item.id, rootCause: item.root_cause };
  return (
    <section className="le-screen" aria-labelledby={`${id}-h`}>
      <ScreenIntro id={`${id}-h`} heading={category} meta={C.meta(item.saved_by || C.unknownUser, fullWhen(item.saved_at))} tags={tags} />
      <SessionContext context={context} manual={manual} />
      <div className="le-field">
        <span className="le-label">{C.pattern}</span>
        <CatalogMatch item={item} />
      </div>
      <DiagnosisStats context={context} />
      {undone && item.undone_by && <p className="le-muted">{C.undoneMeta(item.undone_by, fullWhen(item.undone_at))}</p>}
      <Actions item={item} onChanged={onChanged} />
    </section>
  );
}
